import FinancialAssessment from '@/karehero-app/care/forms/FinancialAssessment';
import { financialAssessmentSceneById } from '@/karehero-app/care/forms/FinancialAssessment.scenes';
import { type HandoverSpec } from '@/designs-dev/handover/types';

// Render the prototype in a named catalog scene (shared with the inspect state
// select, so the handover and the live state switcher can't drift apart).
const scene = (id: string) => (
  <FinancialAssessment {...financialAssessmentSceneById(id).initial} />
);

// Member-side handover for the re-cut Financial status flow (production's
// multi-page care form, redrawn as one question per screen with conditional
// detail disclosed inline). The focus here is the conditional states, how it
// maps to the original, the component changes, and required vs optional — not
// the user stories.
export const financialAssessmentSpec: HandoverSpec = {
  id: 'financial-assessment',
  title: 'Financial status',
  description:
    "The Financial status care form, re-cut as a focused one-question-per-screen flow. Conditional detail is disclosed inline as you answer rather than living on separate pages, and the end screen reviews every answer with in-place editing. Feeds the care experts' bespoke funding guide.",
  links: [
    { label: 'Mini-PRD (Linear)', url: 'https://linear.app/karehero' },
    {
      label: 'Live prototype (Care › Financial status)',
      url: '/karehero-app/forms/financial-assessment',
    },
  ],
  tables: [
    {
      title: 'Conditional fields (progressive disclosure)',
      caption:
        'Each yes/no (or choice) gate reveals its follow-ups inline on the same screen when answered. The gate is always required; the revealed fields are required when shown — except the allergy list, which is optional by design.',
      columns: [
        'Gate question',
        'Answer that reveals',
        'Revealed field(s)',
        'Required?',
      ],
      rows: [
        [
          'Does Margaret have any allergies?',
          'Yes',
          'Allergy multi-select (chips)',
          'Optional',
        ],
        [
          'Does Margaret have any illnesses or disabilities?',
          'Yes',
          'Repeatable illness list (name, how long, medicines, dosage, support)',
          'Required when shown (at least one entry)',
        ],
        [
          'Does Margaret own her home?',
          'Owns it (any option except "No")',
          'Who else lives there · estimated value · property disregard · deferred payment',
          'Required when shown',
        ],
        [
          'Does Margaret have any other income?',
          'Yes',
          'Income source · amount',
          'Required when shown',
        ],
        [
          'Is Margaret paying for any care?',
          'Yes',
          'Monthly cost',
          'Required when shown',
        ],
        [
          'Is a family member caring 35+ hrs/week?',
          'Yes',
          'Is that carer earning > £196/week?',
          'Required when shown',
        ],
      ],
    },
    {
      title: 'Required vs optional',
      caption:
        'Answering every gate is required. Most revealed follow-ups are required when shown; the allergy list is the one deliberately optional field.',
      columns: ['Field', 'Required?', 'Note'],
      rows: [
        [
          'All top-level questions (incl. the allergy & illness yes/no gates)',
          'Required',
          'Must be answered to complete',
        ],
        ['Allergy list', 'Optional', 'Can proceed without listing any'],
        [
          'Illness / condition entries',
          'Required when shown',
          'If they have illnesses, at least one entry is required',
        ],
        [
          'Home, other-income, care-cost, carer-earnings follow-ups',
          'Required when shown',
          'If the gate reveals them, they must be answered',
        ],
      ],
    },
    {
      title: 'How it maps to the original',
      caption:
        'Production is a paginated care form (financial-status + reused care-profile / attendance-allowance questions). This design re-cuts it.',
      columns: ['Original (production)', 'In this design', 'What changed'],
      rows: [
        [
          'Multi-page form, several questions per page',
          'One question per screen with a n/total counter',
          'Re-paginated to a single-question flow',
        ],
        [
          'Conditional questions on their own pages',
          'Follow-ups revealed inline beneath the gate',
          'Progressive disclosure; the total no longer jumps around',
        ],
        [
          'Illness details as repeated inline field groups',
          'Repeatable list: entries collapse to summary cards, add/edit in a bottom sheet',
          'New interaction pattern',
        ],
        [
          'Attendance-allowance "seen a professional / anyone else helps" sub-sections',
          'Not included',
          'Dropped from this flow',
        ],
        [
          'Formal production question copy',
          'Plainer, shorter copy',
          'Rewritten for tone',
        ],
        [
          'Mux intro video on the start screen',
          'Kept (static opening frame in the prototype)',
          'Unchanged — real MuxPlayer ships',
        ],
        [
          'Answers required when relevant (server-gated)',
          'Allergy list made optional (illness details stay required)',
          'Deliberate scope reduction on allergies only',
        ],
      ],
    },
    {
      title: 'Component / interaction changes',
      columns: ['Pattern', 'Before', 'Now'],
      rows: [
        [
          'Multi-select',
          'Plain option list',
          'Chips-in-a-dropdown (chosen options shown as chips inside the field)',
        ],
        [
          'Repeatable illnesses',
          'Inline repeated field groups',
          'Bottom-sheet add/edit; saved entries are read-only summary cards (tap to edit, bin to remove)',
        ],
        [
          'Conditional questions',
          'Separate pages',
          'Inline reveals that scroll the new question to the top',
        ],
        [
          'Editing from the summary',
          'Navigate back to the question page',
          'In-place drawer that also surfaces the conditional fields',
        ],
        [
          'Leaving vs going back',
          'Single back control',
          'Exit (leaves the flow) is distinct from Previous (steps back one question)',
        ],
        ['Support-with-medication', 'Slider', 'Select'],
      ],
    },
    {
      title: 'Behaviour & logic (no separate screen to design)',
      caption:
        'Logic decided here for engineering — reflected in the flow, but with no distinct design state to review.',
      columns: ['Behaviour', 'Source', 'Notes'],
      rows: [
        [
          'Progress saves as you go',
          'Exists in production',
          'Per-step server save; the "Save & exit" control reflects it. No "you’ll lose your progress" step needed.',
        ],
        [
          'Return resumes at the next unanswered required question',
          'New (enhancement)',
          'Production restarts at page 1 with answers pre-filled. The optional allergy list must not count as a blocker.',
        ],
        [
          'All required answered → skip the form, open the review step',
          'New',
          'Production always opens page 1 regardless of completeness.',
        ],
        [
          'Required questions must be answered before advancing',
          'New',
          'Gates and required follow-ups block Next when empty; illness entries need a name.',
        ],
      ],
    },
  ],
  flows: [
    {
      title: 'Complete the assessment',
      steps: [
        {
          id: 'intro',
          title: 'Intro',
          tap: 'Home › Financial status',
          render: () => scene('intro'),
        },
        {
          id: 'name-confirm',
          title: 'Confirm name',
          tap: 'Tap ‘Start’',
          render: () => scene('name-confirm'),
        },
        {
          id: 'home-details',
          title: 'A conditional question',
          tap: 'Answer through to home ownership',
          render: () => scene('home-details'),
        },
        {
          id: 'review',
          title: 'Review answers',
          tap: 'Answer to the end',
          render: () => scene('review'),
        },
      ],
    },
  ],
  states: [
    {
      id: 'intro',
      title: 'Intro screen',
      kind: 'happy',
      repro: 'Scene: Intro. Title, video, points card, Exit + Start',
      render: () => scene('intro'),
    },
    {
      id: 'name-confirm',
      title: 'First question — name on file',
      kind: 'happy',
      repro: 'Scene: name-confirm. Name prefilled from the profile',
      render: () => scene('name-confirm'),
    },
    {
      id: 'name-unknown',
      title: 'First question — no name on file',
      kind: 'edge',
      repro: 'Scene: name-unknown. Name not held, so the step asks for it',
      render: () => scene('name-unknown'),
    },
    {
      id: 'allergies-details',
      title: 'Allergies — details revealed',
      kind: 'edge',
      repro:
        "Scene: allergies-details. 'Yes' reveals the optional allergy multi-select",
      render: () => scene('allergies-details'),
    },
    {
      id: 'home-details',
      title: 'Home ownership — follow-ups revealed',
      kind: 'happy',
      repro:
        'Scene: home-details. Owning the home reveals four required-when-shown follow-ups',
      render: () => scene('home-details'),
    },
    {
      id: 'illness-details',
      title: 'Illnesses — repeatable table',
      kind: 'happy',
      repro:
        'Scene: illness-details. Required list (when they have illnesses); entries collapse to cards, add/edit in a sheet',
      render: () => scene('illness-details'),
    },
    {
      id: 'review',
      title: 'Review answers',
      kind: 'happy',
      repro:
        'Scene: review. Every answer listed, tap a row to edit in a drawer',
      render: () => scene('review'),
    },
  ],
  requirements: [
    {
      text: 'One question per screen with a live n/total counter',
      status: 'built',
      where: 'Every form step',
    },
    {
      text: 'Conditional follow-ups disclosed inline beneath their gate, scrolling into view',
      status: 'built',
      where: 'Allergies, illnesses, home, other income, care cost, carer',
    },
    {
      text: 'Allergy list is optional to fill; illness/condition details are required',
      status: 'built',
      where: 'Allergies vs illnesses steps',
      note: 'The design reflects it (no forcing on allergies; no "tell us later" on illnesses). Enforcement is in the Behaviour table.',
    },
    {
      text: 'Repeatable illnesses: summary cards, add/edit in a bottom sheet, name required per entry',
      status: 'built',
      where: 'Illnesses step',
    },
    {
      text: 'Review screen lists all answers and edits each (incl. conditional fields) in a drawer',
      status: 'built',
      where: 'Review',
    },
    {
      text: 'Save & exit from any question; plain Exit from the intro and review',
      status: 'built',
      where: 'Top-left control',
    },
  ],
  components: {
    mui: [
      'Box',
      'Stack',
      'Typography',
      'TextField (+ select, multiple)',
      'Chip',
      'MenuItem',
      'Card',
      'IconButton',
      'InputAdornment',
    ],
    existing: [
      'Icon (llama)',
      'Button (llama)',
      'PageHeader (llama)',
      'StackPage',
      'BottomSheet',
      'ChoiceCard',
    ],
    new: [
      'FinancialAssessment',
      'Field (kind dispatcher)',
      'Reveal',
      'RepeatableTable',
      'IllnessSummary',
      'ReviewBody',
      'ReviewSection',
      'IntroVideo',
      'MultiSelectDropdown (chips-in-field pattern)',
    ],
  },
  componentsNote:
    'The flow is one component (FinancialAssessment) with a single Field switch dispatching every kind (name, text, date, select, currency, yesno, choice, multiselect chips/dropdown, repeatable table). Content lives in FinancialAssessment.content.ts; states in FinancialAssessment.scenes.ts (shared with the inspect state select). On promotion these become the per-component build tickets.',
};
